import { KpiAppUsageService } from '@/services/kpi';
import { IResponseError } from '@/types/common';
import { handleErrorCommon, notificationMessage, NOTIFY_STATUS } from '@/utils';
import { InboxOutlined } from '@ant-design/icons';
import { Alert, Modal, Typography, Upload } from 'antd';
import { useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { periodKey, toPeriod } from './appUsage';
import { labelOf } from './labels';
import { ParsedUsage, parseUsageFile } from './parseCsv';
import { formatNumber } from '../Dashboard/KpiChart';

const { Text } = Typography;

const SOURCE_LABEL = {
  ga4: 'GA4（画面）',
  appsflyer: 'AppsFlyer（機能）',
  appsflyer_cohort: 'AppsFlyer（新規ファネル）',
} as const;

interface IProps {
  open: boolean;
  /** 取り込み済みの期間のキー（同じ期間を選んだら置き換えになると知らせる） */
  existingKeys: Set<string>;
  onClose: () => void;
  /** 取り込んだ期間のうち最後（新規ファネルは最新の週） */
  onImported: (parsed: ParsedUsage) => void;
  /** 途中の週で失敗したとき。それまでの週は保存済みなので一覧を取り直す */
  onPartiallyImported: () => void;
}

const installsOf = (week: ParsedUsage, platform: 'ios' | 'android') =>
  week.rows.find((r) => r.platform === platform && r.name === 'install')
    ?.users ?? 0;

/**
 * CSV を読んで中身を見せてから取り込む。同じ取得元・期間はサーバー側で丸ごと置き換わる。
 * 新規ファネルは1ファイルに複数週が入るので、古い週から1週ずつ送る。
 */
export const ImportModal: React.FC<IProps> = ({
  open,
  existingKeys,
  onClose,
  onImported,
  onPartiallyImported,
}) => {
  const [parsed, setParsed] = useState<ParsedUsage[] | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // 選び直し・閉じた後に前のファイルの読み取りが終わっても、その結果を使わない
  const readSeq = useRef(0);

  const reset = () => {
    readSeq.current += 1;
    setParsed(null);
    setFileName('');
    setError(null);
  };

  // 途中の週で失敗したら、そこまでの週は取り込み済みのまま止まる（同じファイルを入れ直せば置き換わる）
  const imported = useRef(0);
  const mutation = useMutation(
    async (list: ParsedUsage[]) => {
      imported.current = 0;
      for (const body of list) {
        await KpiAppUsageService.import(body);
        imported.current += 1;
      }
    },
    {
      onSuccess: (_res, list) => {
        const rows = list.reduce((sum, b) => sum + b.rows.length, 0);
        notificationMessage({
          message:
            list.length > 1
              ? `${list.length}週分（${rows}件）を取り込みました。`
              : `${rows}件を取り込みました。`,
          type: NOTIFY_STATUS.SUCCESS,
        });
        onImported(list[list.length - 1]);
        reset();
      },
      // 入力チェック（400）のエラーは項目ごとに返るので、モーダル内にまとめて出す
      onError: (e: IResponseError) => {
        const done = imported.current;
        if (done > 0) onPartiallyImported();
        handleErrorCommon(
          e,
          (field: string, { message }: { message: string }) =>
            setError(
              `${done > 0 ? `${done}週目までは取り込み済みです。以降を` : ''}取り込めませんでした（${field}: ${message}）`,
            ),
        );
      },
    },
  );

  const readFile = async (file: File) => {
    const seq = ++readSeq.current;
    setFileName(file.name);
    setParsed(null);
    setError(null);
    try {
      const result = parseUsageFile(await file.text());
      if (seq === readSeq.current) setParsed(result);
    } catch (e) {
      if (seq === readSeq.current) {
        setError(e instanceof Error ? e.message : 'CSV を読めませんでした。');
      }
    }
  };

  const first = parsed?.[0];
  const isCohort = first?.source === 'appsflyer_cohort';
  const period =
    first && toPeriod(first.source, first.periodStart, first.periodEnd);
  const replaced = parsed?.filter((p) => existingKeys.has(periodKey(p))) ?? [];

  return (
    <Modal
      open={open}
      title='利用状況の取り込み'
      okText='取り込む'
      cancelText='閉じる'
      okButtonProps={{ disabled: !parsed, loading: mutation.isLoading }}
      onOk={() => parsed && mutation.mutate(parsed)}
      // 取り込み中は閉じる・選び直すをさせない（完了時の後始末が、開き直した後の選択を消すため）
      cancelButtonProps={{ disabled: mutation.isLoading }}
      closable={!mutation.isLoading}
      maskClosable={!mutation.isLoading}
      keyboard={!mutation.isLoading}
      onCancel={() => {
        if (mutation.isLoading) return;
        reset();
        onClose();
      }}
      width={640}
      destroyOnClose
    >
      <div className='flex flex-col gap-md'>
        <Upload.Dragger
          accept='.csv,text/csv'
          disabled={mutation.isLoading}
          maxCount={1}
          showUploadList={false}
          beforeUpload={(file) => {
            readFile(file);

            return false;
          }}
        >
          <p className='ant-upload-drag-icon'>
            <InboxOutlined />
          </p>
          <p className='ant-upload-text'>
            {fileName || 'CSV をここにドロップ、またはクリックして選択'}
          </p>
          <p className='ant-upload-hint'>
            GA4「ページとスクリーン」のエクスポート、または AppsFlyer
            用の取り込みファイル（機能・新規ファネル）
          </p>
        </Upload.Dragger>

        {error && <Alert type='error' showIcon message={error} />}

        {parsed && first && period && isCohort && (
          <div className='flex flex-col gap-sm text-[13px]'>
            <div>
              <Text type='secondary'>取得元</Text> {SOURCE_LABEL[first.source]}
              <Text type='secondary' className='ml-lg'>
                週数
              </Text>{' '}
              {parsed.length}
            </div>
            {replaced.length > 0 && (
              <Alert
                type='info'
                showIcon
                message={`${replaced.length}週は取り込み済みです。取り込むと置き換わります。`}
              />
            )}
            <table className='w-full'>
              <thead>
                <tr className='text-fg-muted text-left'>
                  <th className='font-normal py-[2px]'>インストール週</th>
                  <th className='font-normal py-[2px] text-right'>iOS</th>
                  <th className='font-normal py-[2px] text-right'>Android</th>
                  <th className='font-normal py-[2px] text-right'>行数</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((w) => (
                  <tr key={w.periodStart}>
                    <td className='py-[2px]'>
                      {toPeriod(w.source, w.periodStart, w.periodEnd).label}
                    </td>
                    <td className='py-[2px] text-right'>
                      {formatNumber(installsOf(w, 'ios'))}
                    </td>
                    <td className='py-[2px] text-right'>
                      {formatNumber(installsOf(w, 'android'))}
                    </td>
                    <td className='py-[2px] text-right'>{w.rows.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {parsed && first && period && !isCohort && (
          <div className='flex flex-col gap-sm text-[13px]'>
            <div>
              <Text type='secondary'>取得元</Text> {SOURCE_LABEL[first.source]}
              <Text type='secondary' className='ml-lg'>
                期間
              </Text>{' '}
              {period.label}（{first.periodStart} 〜 {first.periodEnd}）
              <Text type='secondary' className='ml-lg'>
                行数
              </Text>{' '}
              {first.rows.length}
            </div>
            {period.kind === 'custom' && (
              <Alert
                type='warning'
                showIcon
                message='月全体（1日〜末日）でも前半（1〜15日）でもない期間です'
                description='取り込めますが、前月との比較と月次の推移には使われません。'
              />
            )}
            {replaced.length > 0 && (
              <Alert
                type='info'
                showIcon
                message='この期間は取り込み済みです。取り込むと置き換わります。'
              />
            )}
            <table className='w-full'>
              <thead>
                <tr className='text-fg-muted text-left'>
                  <th className='font-normal py-[2px]'>名前</th>
                  <th className='font-normal py-[2px] text-right'>ユーザー</th>
                  <th className='font-normal py-[2px] text-right'>
                    {first.source === 'ga4' ? '表示回数' : 'イベント数'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...first.rows]
                  .sort((a, b) => b.users - a.users)
                  .slice(0, 5)
                  .map((r) => (
                    <tr key={r.name}>
                      <td className='py-[2px]'>
                        {labelOf(first.source, r.name).label}
                        <Text type='secondary' className='ml-sm text-[12px]'>
                          {r.name}
                        </Text>
                      </td>
                      <td className='py-[2px] text-right'>
                        {formatNumber(r.users)}
                      </td>
                      <td className='py-[2px] text-right'>
                        {formatNumber(r.count)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {first.rows.length > 5 && (
              <Text type='secondary' className='text-[12px]'>
                ほか {first.rows.length - 5} 件
              </Text>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
